import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { fetchSettings } from '../store/slices/settingsSlice';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Smartphone, Zap, MessageCircle, ShieldCheck, ExternalLink, X } from 'lucide-react';

export const AppDownloadModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const { settings } = useAppSelector((state) => state.settings);
  const [isOpen, setIsOpen] = useState(false);
  const [isWindowMobile, setIsWindowMobile] = useState<boolean | null>(null);

  useEffect(() => {
    const handleResize = () => {
      const isMobileUA = /android|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(navigator.userAgent);
      const isMobileRes = window.innerWidth <= 768;
      setIsWindowMobile(isMobileUA || isMobileRes);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!settings) {
      dispatch(fetchSettings());
    }
  }, [dispatch, settings]);

  const banner = settings?.appBanner;

  useEffect(() => {
    // Check if popup enabled
    if (!banner || banner.showPopup === false) return;

    // Check device targeting resolution filter
    const target = banner.targetDevice || 'mobile_only';
    if (target === 'mobile_only' && isWindowMobile === false) return;
    if (target === 'desktop_only' && isWindowMobile === true) return;

    // Check display frequency
    const todayStr = new Date().toISOString().split('T')[0];
    if (banner.showFrequency === 'once_per_day') {
      const lastShownDate = localStorage.getItem('appPopupLastShownDate');
      if (lastShownDate === todayStr) return;
    } else if (banner.showFrequency === 'once_per_session') {
      const dismissed = sessionStorage.getItem('dismissedAppPopup');
      if (dismissed === 'true') return;
    }

    // Show popup after 2.5s delay
    const timer = setTimeout(() => {
      setIsOpen(true);
    }, 2500);

    return () => clearTimeout(timer);
  }, [banner, isWindowMobile]);

  if (!banner || banner.showPopup === false) {
    return null;
  }

  const target = banner.targetDevice || 'mobile_only';
  if (target === 'mobile_only' && isWindowMobile === false) {
    return null;
  }
  if (target === 'desktop_only' && isWindowMobile === true) {
    return null;
  }

  const playStoreUrl = banner.playStoreUrl || 'https://play.google.com/store/apps/details?id=com.atootbandanmatrimonialapp';
  const popupTitle = banner.popupTitle || 'Get The Official Matrimonial App!';
  const popupText = banner.popupText || 'Enjoy instant notifications, seamless chat, and faster match discovery on our official App.';
  const buttonText = banner.buttonText || 'Download App';
  const badgeText = banner.badgeText || 'GET OUR APP';

  const handleClose = () => {
    setIsOpen(false);
    const todayStr = new Date().toISOString().split('T')[0];
    localStorage.setItem('appPopupLastShownDate', todayStr);
    sessionStorage.setItem('dismissedAppPopup', 'true');
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="w-[92vw] max-w-md p-0 overflow-hidden border-0 shadow-2xl rounded-3xl bg-white mx-auto">
        {/* Header Banner Background */}
        <div className="bg-gradient-to-r from-rose-600 via-pink-600 to-purple-600 p-6 text-white text-center relative overflow-hidden">
          <button
            onClick={handleClose}
            className="absolute top-3 right-3 p-1.5 rounded-full bg-black/20 hover:bg-black/40 text-white/90 transition-all focus:outline-none"
          >
            <X className="h-4 w-4" />
          </button>
          
          <div className="w-16 h-16 mx-auto bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center mb-3 border border-white/30 shadow-inner animate-bounce">
            <Smartphone className="h-9 w-9 text-white" />
          </div>

          <Badge className="bg-white/20 text-white border-0 text-xs uppercase tracking-widest px-3 py-1 font-bold mb-2 inline-flex items-center gap-1">
            <Zap className="h-3 w-3 fill-amber-300 text-amber-300" />
            {badgeText}
          </Badge>

          <DialogTitle className="text-xl sm:text-2xl font-bold tracking-tight text-white mt-1">
            {popupTitle}
          </DialogTitle>
        </div>

        {/* Modal Content Body */}
        <div className="p-6 space-y-5">
          <DialogDescription className="text-gray-600 text-sm text-center leading-relaxed font-normal">
            {popupText}
          </DialogDescription>

          {/* App Highlights */}
          <div className="grid grid-cols-3 gap-2 py-2 border-y border-gray-100">
            <div className="flex flex-col items-center text-center p-2 rounded-xl bg-rose-50/50">
              <Zap className="h-5 w-5 text-rose-600 mb-1" />
              <span className="text-[11px] font-semibold text-gray-700">Faster Matches</span>
            </div>
            <div className="flex flex-col items-center text-center p-2 rounded-xl bg-pink-50/50">
              <MessageCircle className="h-5 w-5 text-pink-600 mb-1" />
              <span className="text-[11px] font-semibold text-gray-700">Instant Chat</span>
            </div>
            <div className="flex flex-col items-center text-center p-2 rounded-xl bg-purple-50/50">
              <ShieldCheck className="h-5 w-5 text-purple-600 mb-1" />
              <span className="text-[11px] font-semibold text-gray-700">100% Verified</span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-2 pt-1">
            <a
              href={playStoreUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleClose}
              className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-rose-600 to-pink-600 hover:from-rose-700 hover:to-pink-700 text-white font-bold py-3 px-4 rounded-xl shadow-lg hover:shadow-xl transition-all text-sm active:scale-98"
            >
              <span>{buttonText}</span>
              <ExternalLink className="h-4 w-4" />
            </a>

            <Button
              variant="ghost"
              onClick={handleClose}
              className="w-full text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100"
            >
              Remind Me Later
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AppDownloadModal;
